import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UPCLOUD_ZONES, useProvisionVm, type ProvisionVmBody } from '@/hooks/use-provision-vm';
import { ApiError } from '@/lib/api-client';

const NAME_RE = /^(exit-[a-z]{2}-[a-z0-9]+|bootstrap-[a-z0-9]+)-\d{2}$/;
const PLANS: NonNullable<ProvisionVmBody['plan']>[] = ['1xCPU-2GB', '2xCPU-4GB', '1xCPU-1GB'];

// Matches the native <select> styling `add-server-dialog.tsx` already uses —
// a shadcn/Radix Select isn't label-associated for `getByLabelText`/`fireEvent.change`.
const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-border bg-card px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40';

interface Props {
  /** Called with the Semaphore task id once the create is dispatched. */
  onDispatched: (taskId: number, name: string) => void;
}

/**
 * SPEC-0019 — "Create at provider": the VM does not exist yet. The API
 * dispatches a Semaphore Terraform run; the server row appears when the
 * playbook's callback lands (~2–3 min), and provisioning follows by itself.
 */
export function CreateAtProviderForm({ onDispatched }: Props): JSX.Element {
  const [name, setName] = useState('');
  const [role, setRole] = useState<ProvisionVmBody['role']>('exit_node');
  const [zone, setZone] = useState('pl-waw1');
  const [plan, setPlan] = useState<NonNullable<ProvisionVmBody['plan']>>('1xCPU-2GB');
  const [stealth, setStealth] = useState(false);
  const [floatingIp, setFloatingIp] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const provisionVm = useProvisionVm();

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!NAME_RE.test(name)) {
      setNameError('Name must be exit-<cc>-<loc>-NN or bootstrap-<loc>-NN (e.g. bootstrap-waw-01).');
      return;
    }
    setNameError(null);
    provisionVm.mutate(
      {
        name,
        provider: 'upcloud',
        role,
        zone,
        plan,
        capabilities: stealth ? ['stealth'] : ['standard'],
        enable_floating_ip: floatingIp,
      },
      {
        onSuccess: (res) => {
          toast.success(`Creating ${name} at UpCloud (${zone}) — task #${res.task_id}`);
          onDispatched(res.task_id, name);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : 'Provision request failed');
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      <p className="text-sm text-muted-foreground">
        Creates the VM with Terraform (module <code>exits/upcloud</code>), registers it, and runs the
        role&apos;s provisioning playbook. The server appears in the list once the VM answers on SSH.
      </p>
      <div className="space-y-1">
        <Label htmlFor="pv-name">Server name</Label>
        <Input
          id="pv-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value.trim());
            setNameError(null);
          }}
          placeholder="bootstrap-waw-01"
          className="font-mono"
        />
        {nameError && (
          <p className="text-sm text-destructive" role="alert">
            {nameError}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="pv-role">Role</Label>
        <select
          id="pv-role"
          className={SELECT_CLASS}
          value={role}
          onChange={(e) => setRole(e.target.value as ProvisionVmBody['role'])}
        >
          <option value="exit_node">Exit node</option>
          <option value="bootstrap_edge">Bootstrap edge</option>
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="pv-zone">Zone</Label>
        <select id="pv-zone" className={SELECT_CLASS} value={zone} onChange={(e) => setZone(e.target.value)}>
          {UPCLOUD_ZONES.map((z) => (
            <option key={z.id} value={z.id}>
              {z.country} — {z.label} ({z.id})
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="pv-plan">Plan</Label>
        <select
          id="pv-plan"
          className={SELECT_CLASS}
          value={plan}
          onChange={(e) => setPlan(e.target.value as NonNullable<ProvisionVmBody['plan']>)}
        >
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={stealth} onChange={(e) => setStealth(e.target.checked)} /> Stealth
        capability
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={floatingIp} onChange={(e) => setFloatingIp(e.target.checked)} /> Allocate
        a floating egress IP (durable; cannot be destroyed from the dashboard)
      </label>
      <div className="flex justify-end pt-1">
        <Button type="submit" disabled={provisionVm.isPending}>
          {provisionVm.isPending ? 'Dispatching…' : 'Create at UpCloud'}
        </Button>
      </div>
    </form>
  );
}
